import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import '../styles/JobStatus.css';

function JobStatus() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    loadJob();
    // Poll for updates every 30 seconds if job is in progress
    const interval = setInterval(() => {
      if (job && ['pending', 'running'].includes(job.status)) {
        loadJob(true);
      }
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const loadJob = async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await api.get(`/api/jobs/${jobId}`);
      setJob(data);
    } catch (err) {
      console.error('Failed to load job:', err);
      if (err.status === 404) {
        setError('Job not found');
      } else if (err.status === 403) {
        setError('You don\'t have access to this job');
      } else {
        setError('Failed to load job details');
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadAllArtifacts = async () => {
    setDownloading(true);
    try {
      // Download as single ZIP file
      const response = await fetch(`/api/jobs/${jobId}/artifacts/zip`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          alert('No files available for download.');
        } else {
          throw new Error('Download failed');
        }
        return;
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${jobId}-deliverables.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download artifacts:', err);
      alert('Failed to download files. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const downloadFormat = async (format) => {
    try {
      const response = await fetch(`/api/jobs/${jobId}/convert?format=${format}`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          alert('Document not available in this format.');
        } else {
          throw new Error('Download failed');
        }
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const extensions = { txt: 'txt', pdf: 'pdf', docx: 'docx' };
      a.download = `${jobId}-deliverable.${extensions[format] || format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error(`Failed to download ${format}:`, err);
      alert('Failed to download. Please try again.');
    }
  };

  const goToConversation = () => {
    // Reset mode to conversations so Chat.jsx shows the conversation view
    try { localStorage.setItem('tim-sidebar-mode', 'conversations'); } catch {}
    // Navigate to the conversation if we have it, otherwise fall back to chat
    if (job && job.conversation_id) {
      navigate(`/chat/${job.conversation_id}`);
    } else {
      navigate('/chat');
    }
  };

  const formatCents = (cents) => {
    if (cents === null || cents === undefined) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    // Ensure UTC interpretation for timestamps without timezone
    let isoString = dateStr;
    if (!isoString.endsWith('Z') && !isoString.match(/[+-]\d{2}(:\d{2})?$/)) {
      isoString = isoString + 'Z';
    }
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      pending: { label: 'Pending', color: 'status-pending', icon: '⏳' },
      running: { label: 'In Progress', color: 'status-running', icon: '⚡' },
      done: { label: 'Complete', color: 'status-done', icon: '✅' },
      failed: { label: 'Failed', color: 'status-failed', icon: '❌' },
      awaiting_review: { label: 'Under Review', color: 'status-review', icon: '👀' },
      revision: { label: 'In Revision', color: 'status-revision', icon: '🔄' },
      budget_exhausted: { label: 'Budget Limit', color: 'status-failed', icon: '💰' },
      escalated: { label: 'Escalated', color: 'status-escalated', icon: '🚨' }
    };
    return statusMap[status] || { label: status, color: 'status-pending', icon: '❓' };
  };

  const getSubtaskStatusInfo = (status) => {
    const statusMap = {
      pending: { label: 'Pending', color: 'subtask-pending' },
      running: { label: 'Running', color: 'subtask-running' },
      done: { label: 'Done', color: 'subtask-done' },
      failed: { label: 'Failed', color: 'subtask-failed' }
    };
    return statusMap[status] || { label: status, color: 'subtask-pending' };
  };

  if (loading) {
    return (
      <div className="job-status-page">
        <div className="job-status-loading">
          <div className="loading-spinner"></div>
          <p>Loading job details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="job-status-page">
        <div className="job-status-error">
          <h2>😕 {error}</h2>
          <p>The job you're looking for might not exist or you may not have permission to view it.</p>
          <Link to="/" className="btn btn-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  const subtasks = job.subtasks || [];
  const completedSubtasks = subtasks.filter(s => s.status === 'done').length;
  const totalSubtasks = subtasks.length;
  const progressPercent = totalSubtasks > 0 ? (completedSubtasks / totalSubtasks) * 100 : 0;
  const statusInfo = getStatusInfo(job.status);

  // Human-facing title: use job.title, or truncate goal to first sentence / ~80 chars
  const getDisplayTitle = () => {
    if (job.title) return job.title;
    if (!job.goal) return 'Project';
    // First sentence (up to first period followed by space or end)
    const firstSentence = job.goal.match(/^.+?(?:\.\s|[!?]|$)/); 
    const truncated = firstSentence ? firstSentence[0] : job.goal;
    return truncated.length > 80 ? truncated.slice(0, 77) + '...' : truncated;
  };

  // Strip agent instructions from subtask descriptions
  const cleanSubtaskDescription = (desc, index) => {
    if (!desc) return `Task ${index + 1}`;
    // Strip everything from "Requirements:" onward
    const reqIndex = desc.indexOf('Requirements:');
    const cleaned = reqIndex >= 0 ? desc.slice(0, reqIndex).trim() : desc;
    // Also strip from "Customer requirements:" onward
    const custReqIndex = cleaned.indexOf('Customer requirements:');
    const final = custReqIndex >= 0 ? cleaned.slice(0, custReqIndex).trim() : cleaned;
    // If nothing meaningful remains, use a generic label
    if (!final || final.length < 5) return `Task ${index + 1}`;
    // Take first sentence if still long
    const sentence = final.match(/^.+?(?:\.\s|[!?]|$)/);
    return sentence ? sentence[0] : final;
  };

  const displayTitle = getDisplayTitle();

  return (
    <div className="job-status-page">
      <div className="job-status-container">
        {/* Header */}
        <header className="job-status-header">
          <div className="job-id-row">
            <span className="job-id">Job #{job.id}</span>
            <span className={`status-badge ${statusInfo.color}`}>
              {statusInfo.icon} {statusInfo.label}
            </span>
          </div>
          <h1 className="job-title">{displayTitle}</h1>
          {job.goal && displayTitle !== job.goal && (
            <p className="job-description">{job.goal}</p>
          )}
        </header>

        {/* Revision indicator */}
        {job.parent_job_id && (
          <div className="revision-indicator" style={{
            padding: '8px 16px',
            marginBottom: '16px',
            background: 'var(--bg-secondary, #f5f5f5)',
            borderRadius: '8px',
            fontSize: '14px',
            color: 'var(--text-muted, #666)'
          }}>
            🔄 This is a revision of{' '}
            <Link to={`/job/${job.parent_job_id}`} style={{color: 'var(--primary, #4f46e5)'}}>
              Job #{job.parent_job_id}
            </Link>
          </div>
        )}

        {/* Progress Section */}
        <section className="job-progress-section">
          <div className="progress-header">
            <span className="progress-label">Progress</span>
            <span className="progress-count">{completedSubtasks} of {totalSubtasks} tasks complete</span>
          </div>
          <div className="progress-bar-container">
            <div 
              className="progress-bar-fill"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </section>

        {/* Stats Grid */}
        <section className="job-stats-grid">
          <div className="stat-card">
            <span className="stat-label">Created</span>
            <span className="stat-value stat-date">{formatDate(job.created_at)}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Completed</span>
            <span className="stat-value stat-date">
              {job.completed_at ? formatDate(job.completed_at) : 'In progress...'}
            </span>
          </div>
        </section>

        {/* Deliverables (if complete) */}
        {job.status === 'done' && (
          <section className="job-deliverables">
            <h2>📦 Deliverables</h2>
            <div className="deliverable-card">
              <p>Your project is complete! View or download your deliverables below.</p>
              {job.domain && job.domain_status === 'active' && (
                <p className="preview-link">
                  <a 
                    href={`https://${job.domain}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    🌐 View Live Site ({job.domain})
                  </a>
                </p>
              )}
              <div className="deliverable-actions deliverable-actions-row">
                {job.preview_url && (
                  <a 
                    href={job.preview_url.startsWith('http') ? job.preview_url : `https://${job.preview_url}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline"
                  >
                    🌐 View Preview
                  </a>
                )}
                <button 
                  className="btn btn-primary" 
                  onClick={downloadAllArtifacts}
                  disabled={downloading}
                >
                  {downloading ? '⏳ Downloading...' : '📥 Download All'}
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={goToConversation}
                >
                  💬 Return to Chat
                </button>
              </div>
              {job.is_document_job && (
                <div className="format-downloads" style={{marginTop: '16px'}}>
                  <p style={{fontSize: '14px', marginBottom: '8px', color: 'var(--text-muted)'}}>
                    Download as:
                  </p>
                  <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                    <button onClick={() => downloadFormat('txt')} className="btn btn-outline">
                      📄 Plain Text
                    </button>
                    <button onClick={() => downloadFormat('pdf')} className="btn btn-outline">
                      📕 PDF
                    </button>
                    <button onClick={() => downloadFormat('docx')} className="btn btn-outline">
                      📝 Word
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Subtasks */}
        <section className="job-subtasks">
          <h2>🔧 Tasks</h2>
          {subtasks.length === 0 ? (
            <div className="no-subtasks">
              <p>No tasks have been created yet.</p>
            </div>
          ) : (
            <div className="subtask-list">
              {subtasks.map((subtask, index) => {
                const subtaskStatus = getSubtaskStatusInfo(subtask.status);
                return (
                  <div key={subtask.id} className="subtask-item">
                    <div className="subtask-number">{index + 1}</div>
                    <div className="subtask-content">
                      <div className="subtask-header">
                        <span className="subtask-description">{cleanSubtaskDescription(subtask.description, index)}</span>
                        <span className={`subtask-status ${subtaskStatus.color}`}>
                          {subtaskStatus.label}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Timeline placeholder */}
        <section className="job-timeline">
          <h2>📋 Activity</h2>
          <div className="timeline-list">
            <div className="timeline-item">
              <span className="timeline-dot"></span>
              <span className="timeline-content">
                <strong>Job created</strong>
                <span className="timeline-date">{formatDate(job.created_at)}</span>
              </span>
            </div>
            {job.status === 'running' && (
              <div className="timeline-item">
                <span className="timeline-dot active"></span>
                <span className="timeline-content">
                  <strong>Work in progress</strong>
                  <span className="timeline-date">Now</span>
                </span>
              </div>
            )}
            {job.completed_at && (
              <div className="timeline-item">
                <span className="timeline-dot done"></span>
                <span className="timeline-content">
                  <strong>Job completed</strong>
                  <span className="timeline-date">{formatDate(job.completed_at)}</span>
                </span>
              </div>
            )}
          </div>
        </section>

        {/* Help Section */}
        <section className="job-help">
          <p>
            Questions about your job? <a href="mailto:support@theideamachine.dev">Contact support</a>
          </p>
        </section>
      </div>
    </div>
  );
}

export default JobStatus;
